/**
 * 簡訊邀請連結的「跨流程攜帶」工具。
 *
 * 使用情境：瓦斯行以簡訊寄出 LIFF 邀請連結（帶 `phone`、`invite`、選填 `order_id`），
 * 客戶點擊後在 LINE 內開啟。未註冊者會走 LINE login 的 redirect round-trip，
 * 期間 URL query 會遺失，因此進站時先把參數存進 sessionStorage 攜帶，
 * 登入完成後由 /sms-invite 落地頁讀取，靜默完成「註冊 + 綁定 + 直達訂單詳情」。
 *
 * sessionStorage 可跨 LIFF login 的 redirect round-trip 與內部 navigate 保留。
 *
 * LINE 推送邀請的連結沒有 `phone`、改帶 `k`（link token）。
 *
 * 比照 familyInvite.ts / shopInviteCode.ts 的寫法。
 */
const KEY = "sms_invite";

export interface SmsInvitePayload {
  /** 受邀客戶手機（簡訊路徑）；LINE 推送路徑沒有 */
  phone?: string;
  /** 簡訊邀請碼（後端驗證邀請有效性）。 */
  invite: string;
  /** LINE 推送路徑的 link token；沒有 phone 時後端用它驗身分 */
  k?: string;
  /** 選填：關聯的訂單 platform_order_id，供落地後直達訂單詳情。 */
  order_id?: string;
  /** 選填：邀請來源。'broadcast'（平台廣發）時註冊後導首頁而非訂單頁。 */
  src?: string;
}

/** 淨化字串：去除前後空白；取不到回空字串。 */
const clean = (raw: string | null | undefined): string => (raw ?? "").trim();

/** 從一組 key/value 取值組出 payload；invite 必填，phone 或 k 其一必填，否則 null。 */
const buildPayload = (get: (key: string) => string | null | undefined): SmsInvitePayload | null => {
  const invite = clean(get("invite"));
  const phone = clean(get("phone"));
  const k = clean(get("k"));
  if (!invite || (!phone && !k)) return null;
  const payload: SmsInvitePayload = { invite };
  if (phone) payload.phone = phone;
  if (k) payload.k = k;
  const orderId = clean(get("order_id"));
  if (orderId) payload.order_id = orderId;
  const src = clean(get("src"));
  if (src) payload.src = src;
  return payload;
};

/** 進站時呼叫：若目前 URL 帶 `phone` / `invite` 或 `k` / `invite`，存進 sessionStorage 以跨流程攜帶。
 *  預設讀目前網址的 query；短連結中繼頁（/s/{token}）自己的網址沒有這些參數（換 token 才拿得到），
 *  故可傳入自組的 query string 覆蓋，行為與「讀到目前網址」等價。 */
export const captureSmsInviteFromUrl = (
  search: string = typeof window !== "undefined" ? window.location.search : "",
): void => {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(search);
    const payload = buildPayload((key) => params.get(key));
    if (!payload) return;
    sessionStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    // 忽略：取不到就當作沒有簡訊邀請參數
  }
};

/** 從目前網址直接讀簡訊邀請參數；不完整則 null。 */
const readFromUrl = (): SmsInvitePayload | null => {
  try {
    const params = new URLSearchParams(window.location.search);
    return buildPayload((key) => params.get(key));
  } catch {
    return null;
  }
};

/**
 * 取得已攜帶的簡訊邀請資料；沒有或格式錯誤則 null。
 *
 * 優先讀網址：LINE login 會帶 redirectUri 回到本頁（含 query），參數本來就還在，
 * 這條路不受 sessionStorage 在 webview round-trip 後遺失的影響。網址沒有才退回 storage。
 */
export const getSmsInvite = (): SmsInvitePayload | null => {
  if (typeof window === "undefined") return null;
  const fromUrl = readFromUrl();
  if (fromUrl) return fromUrl;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SmsInvitePayload>;
    return buildPayload((key) => (parsed as Record<string, string | undefined>)[key]);
  } catch {
    return null;
  }
};

/** 用掉/離開後清除，避免殘留影響後續流程。 */
export const clearSmsInvite = (): void => {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(KEY);
};

/** 送出鎖 key 前綴（實際 key 需接上 invite_code，避免不同邀請互相卡住）。 */
const SUBMIT_LOCK_PREFIX = "sms_invite_submitting:";

/** 送出鎖視為過期的時間（毫秒）：保底機制，避免頁面在請求途中被關閉導致永久死鎖。 */
const SUBMIT_LOCK_TTL_MS = 30_000;

/**
 * 取得送出鎖的剩餘存活毫秒數（同一 invite_code）；0 代表目前未鎖（沒有鎖或已過期）。
 *
 * 提供給呼叫端排「鎖過期後重新觸發送出」的 timeout 用，
 * 避免先發請求的 instance 已消滅（頁面 reload / LIFF 重導向）時，
 * 鎖過期後沒有任何東西重新觸發送出，使用者卡在 spinner。
 */
export const getSmsInviteSubmitLockRemainingMs = (inviteCode: string): number => {
  if (typeof window === "undefined") return 0;
  try {
    const raw = sessionStorage.getItem(SUBMIT_LOCK_PREFIX + inviteCode);
    if (!raw) return 0;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return 0;
    const remaining = SUBMIT_LOCK_TTL_MS - (Date.now() - ts);
    return remaining > 0 ? remaining : 0;
  } catch {
    return 0;
  }
};

/**
 * 檢查是否已有「進行中」的送出鎖（同一 invite_code、30 秒內）。
 *
 * 用於防止 component remount（LIFF / LINE login 流程會發生）造成 submittedRef 失效、
 * 同一次點擊在極短時間內對後端送出兩次相同的註冊請求。
 */
export const isSmsInviteSubmitLocked = (inviteCode: string): boolean =>
  getSmsInviteSubmitLockRemainingMs(inviteCode) > 0;

/** 標記送出鎖：送出請求前呼叫，寫入目前時間戳。 */
export const lockSmsInviteSubmit = (inviteCode: string): void => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(SUBMIT_LOCK_PREFIX + inviteCode, String(Date.now()));
  } catch {
    // 忽略：寫入失敗最壞情況只是失去這道保護
  }
};

/** 清除送出鎖：請求成功或失敗（決策完成）時呼叫。 */
export const unlockSmsInviteSubmit = (inviteCode: string): void => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(SUBMIT_LOCK_PREFIX + inviteCode);
  } catch {
    // 忽略
  }
};
